/**
 * Create Registry — Phase F8 (agentic smart-create)
 *
 * Maps a module / natural-language intent onto the concrete create surface:
 *   - formId       : the `data-copilot-form` id (also the schema id in formSchemas)
 *   - route        : the SPA route whose page hosts the create dialog/form
 *   - autoOpenCreate : whether the host page listens for `copilot:openCreate`
 *   - createPath   : the backend endpoint used by a `createRecord` plan step
 *   - entityType   : the knowledge-graph entity type (search / suggest-links)
 *
 * The registry reuses the existing formSchemas registry for the field list, so
 * a single source of truth drives both the HITL diff and the planner payload.
 */

import {
  getFormSchema,
  listFormSchemas,
  type FormSchema,
} from './formSchemas'
import type {
  AgentAvailableForm,
  FormSchemaField,
} from '@/services/copilot.service'

export interface CreateEntry {
  /** Canonical module key (matches moduleContext segments where possible). */
  module: string
  /** French keywords used to resolve a free-text "Créer …" prompt. */
  keywords: string[]
  /** data-copilot-form id + formSchemas id. */
  formId: string
  /** Route whose page renders the create form. */
  route: string
  /** True when the host page auto-opens its create dialog on `copilot:openCreate`. */
  autoOpenCreate: boolean
  /** Backend create endpoint (relative to the api-gateway /api/v1). */
  createPath: string
  /** Knowledge entity type for search / suggest-links. */
  entityType: string
  /** Human label shown in the palette / sidebar. */
  title: string
  /**
   * Rename map applied to a `createRecord` body before POST: planner/schema
   * field name → backend field name (e.g. a control's `name` → `title`). Keys
   * NOT in the map pass through unchanged, so nothing is silently dropped.
   */
  fieldMap?: Record<string, string>
  /**
   * Constant fields folded into every `createRecord` body for this entry — used
   * for backend discriminators the form doesn't surface (e.g. a normative
   * document's `type: 'policy'`).
   */
  fixedFields?: Record<string, unknown>
}

// Ordered by resolution priority (first keyword hit wins).
const CREATE_ENTRIES: CreateEntry[] = [
  {
    module: 'risk',
    keywords: ['risque', 'risk', 'menace', 'vulnérabilité risque'],
    formId: 'risk-create',
    route: '/risks',
    autoOpenCreate: true,
    createPath: '/risk-pro/risks',
    entityType: 'risk',
    title: 'Risque',
  },
  {
    module: 'security',
    keywords: ['incident', 'intrusion', 'sécurité incident', 'breach', 'faille'],
    formId: 'incident-create',
    route: '/security-pro/incidents',
    autoOpenCreate: true,
    createPath: '/security-pro/incidents',
    entityType: 'incident',
    title: 'Incident de sécurité',
  },
  {
    module: 'compliance',
    keywords: ['pia', 'aipd', 'efvp', 'analyse d’impact', 'analyse impact', 'privacy'],
    formId: 'pia-create',
    route: '/compliance-pro/pia',
    autoOpenCreate: false,
    createPath: '/compliance-pro/pia',
    entityType: 'pia',
    title: 'PIA / AIPD',
  },
  {
    module: 'audit',
    keywords: ['constatation', 'finding', 'audit finding', 'anomalie audit'],
    formId: 'finding-create',
    route: '/audit-pro/findings',
    autoOpenCreate: false,
    createPath: '/audit-pro/findings',
    entityType: 'finding',
    title: 'Constatation d’audit',
  },
  {
    module: 'strategy',
    keywords: ['business case', 'cas d’affaire', 'cas daffaire', 'analyse de rentabilité'],
    formId: 'business-case-create',
    route: '/strategy-pro/business-case',
    autoOpenCreate: false,
    // Was `/strategy/business-cases` (404 at gateway). The persisting, round-tripping
    // create surface is the governance-service strategy-pro path used by the page.
    createPath: '/strategy-pro/business-case',
    entityType: 'business_case',
    title: 'Business case',
  },
  {
    module: 'assessment',
    keywords: ['évaluation', 'evaluation', 'assessment', 'questionnaire', 'maturité'],
    formId: 'assessment-create',
    route: '/assessments/create',
    autoOpenCreate: false,
    createPath: '/assessments',
    entityType: 'assessment',
    title: 'Évaluation',
  },
  {
    module: 'governance',
    keywords: ['politique', 'policy', 'normative', 'directive'],
    formId: 'policy-create',
    route: '/governance-pro/policies',
    autoOpenCreate: true,
    createPath: '/governance-pro/normative-documents',
    entityType: 'policy',
    title: 'Politique',
    // Planner emits `domain` (schema) → backend column is `domainId`; the page's
    // own create sends `type: 'policy'`, so the agent path must inject it too.
    fieldMap: { domain: 'domainId', name: 'title' },
    fixedFields: { type: 'policy' },
  },
  {
    module: 'control',
    keywords: ['contrôle', 'controle', 'control', 'mesure de contrôle', 'security_control', 'security control'],
    formId: 'control-create',
    route: '/compliance-pro/controls',
    autoOpenCreate: true,
    createPath: '/compliance-pro/controls',
    entityType: 'control',
    // Backend column is `title`; drop-in for a planner that emits `name`.
    fieldMap: { name: 'title' },
    title: 'Contrôle',
  },
  {
    module: 'obligation',
    keywords: [
      'obligation', 'exigence', 'obligation réglementaire', 'obligation reglementaire',
      'exigence réglementaire', 'exigence reglementaire',
    ],
    formId: 'obligation-create',
    route: '/compliance-pro/register',
    autoOpenCreate: true,
    createPath: '/compliance-pro/obligations',
    entityType: 'obligation',
    // Backend column is `requirement`; map both common title-ish aliases onto it.
    fieldMap: { title: 'requirement', name: 'requirement' },
    title: 'Obligation',
  },
  {
    module: 'vendor',
    keywords: ['fournisseur', 'vendor', 'tiers', 'prestataire', 'sous-traitant', 'sous traitant'],
    formId: 'vendor-create',
    route: '/vendor-pro/catalogue',
    autoOpenCreate: true,
    createPath: '/vendor-pro/catalogue',
    entityType: 'vendor',
    // Backend column is `name`; map a planner that emits `title`.
    fieldMap: { title: 'name' },
    title: 'Fournisseur',
  },
]

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

/** All registered create entries. */
export function listCreateEntries(): CreateEntry[] {
  return CREATE_ENTRIES
}

/** Resolve a create entry by exact module key (segment of the current route). */
export function getCreateEntryByModule(module?: string): CreateEntry | undefined {
  if (!module) return undefined
  const m = normalize(module)
  return CREATE_ENTRIES.find(
    (e) => normalize(e.module) === m || normalize(e.route).includes(m),
  )
}

/** Resolve a create entry by formId. */
export function getCreateEntryByFormId(formId: string): CreateEntry | undefined {
  return CREATE_ENTRIES.find((e) => e.formId === formId)
}

/**
 * Resolve a create entry by its knowledge `entityType` (e.g. 'policy',
 * 'control', 'incident'). Also tolerates a few common planner aliases so a
 * `createRecord` with entity_type='security_control' lands on the control
 * entry rather than the risk fallback.
 */
export function getCreateEntryByEntityType(entityType?: string): CreateEntry | undefined {
  if (!entityType) return undefined
  const e = normalize(entityType)
  const direct = CREATE_ENTRIES.find((c) => normalize(c.entityType) === e)
  if (direct) return direct
  // Alias fallbacks: match on a keyword or the module segment.
  return CREATE_ENTRIES.find(
    (c) => normalize(c.module) === e || c.keywords.some((kw) => e.includes(normalize(kw))),
  )
}

/**
 * Resolve the best create entry for a free-text prompt / module hint, or
 * `null` when nothing sensible matches. Callers should surface the ambiguity to
 * the user instead of silently defaulting (which used to always be the RISK
 * form — #AG2/#AG3).
 */
export function resolveCreateEntryOrNull(text: string, moduleHint?: string): CreateEntry | null {
  const n = normalize(text)
  const scored = CREATE_ENTRIES.map((e) => {
    // Score by the LONGEST matching keyword so specific phrases (e.g.
    // "obligation réglementaire") outrank generic ones (e.g. "risque").
    const best = e.keywords.reduce((max, kw) => {
      const nk = normalize(kw)
      return n.includes(nk) && nk.length > max ? nk.length : max
    }, 0)
    return { e, score: best }
  }).sort((a, b) => b.score - a.score)

  if (scored[0]?.score > 0) return scored[0].e
  const byModule = getCreateEntryByModule(moduleHint)
  return byModule ?? null
}

/**
 * Resolve the best create entry for a free-text prompt / module hint.
 * Falls back to the risk entry when nothing else matches so legacy callers
 * always land on a working create form. Prefer `resolveCreateEntryOrNull` for
 * user-facing flows so an unresolved intent is reported rather than mis-routed.
 */
export function resolveCreateEntry(text: string, moduleHint?: string): CreateEntry {
  return resolveCreateEntryOrNull(text, moduleHint) ?? CREATE_ENTRIES[0]
}

/**
 * Apply an entry's `fieldMap` (rename) + `fixedFields` (constants) to a
 * createRecord body so it matches the backend contract without dropping any
 * planner-provided values.
 */
export function applyCreateFieldMap(
  entry: CreateEntry | undefined,
  body: Record<string, unknown>,
): Record<string, unknown> {
  if (!entry) return body
  const out: Record<string, unknown> = {}
  const map = entry.fieldMap ?? {}
  for (const [k, v] of Object.entries(body)) {
    const target = map[k] ?? k
    // Don't clobber an already-mapped value with an unmapped duplicate.
    if (out[target] === undefined) out[target] = v
  }
  for (const [k, v] of Object.entries(entry.fixedFields ?? {})) {
    if (out[k] === undefined) out[k] = v
  }
  return out
}

// ── Planner payload helpers ───────────────────────────────────────────────────

/** Convert a FormSchema into the planner's `available_forms` shape. */
export function formSchemaToAvailableForm(schema: FormSchema, entityType: string): AgentAvailableForm {
  return {
    form_id: schema.formId,
    entity_type: entityType,
    fields: schema.fields.map((f) => ({
      name: f.name,
      label: f.label,
      type: f.type,
      ...(f.options ? { enum: f.options.map((o) => o.value) } : {}),
    })),
  }
}

/** Convert a FormSchema into the fill-form endpoint's `form_schema` shape. */
export function formSchemaToFillSchema(schema: FormSchema): FormSchemaField[] {
  return schema.fields.map((f) => ({
    name: f.name,
    label: f.label,
    type: f.type,
    required: f.required,
    ...(f.options ? { enum: f.options.map((o) => o.value) } : {}),
  }))
}

/**
 * Build the `available_forms` list handed to the planner: the create form for
 * the current module first (if any), then the rest of the registry so the plan
 * can pivot to another entity when the goal demands it.
 */
export function buildAvailableForms(moduleHint?: string): AgentAvailableForm[] {
  const current = getCreateEntryByModule(moduleHint)
  const ordered = [
    ...(current ? [current] : []),
    ...CREATE_ENTRIES.filter((e) => e !== current),
  ]
  const out: AgentAvailableForm[] = []
  for (const entry of ordered) {
    const schema = getFormSchema(entry.formId)
    if (schema) out.push(formSchemaToAvailableForm(schema, entry.entityType))
  }
  return out
}

/** Fallback: every known form schema as an available form (entity_type = formId). */
export function allAvailableForms(): AgentAvailableForm[] {
  return listFormSchemas().map((s) => {
    const entry = getCreateEntryByFormId(s.formId)
    return formSchemaToAvailableForm(s, entry?.entityType ?? s.formId)
  })
}
